import React from "react";
import { Button } from "../ui/button";
import { FilePdf, FileDoc, Printer } from "@phosphor-icons/react";
import AdsCards from "../AdsCards";
import WhyThisMatters from "../WhyThisMatters";
import { API } from "../../lib/api";
import { track } from "../../lib/analytics";
import { useMarketplaceEnabled } from "../../context/ConfigContext";

const Section = ({ num, title, children }) => (
  <div className="mt-8">
    <div className="text-center font-serif-display font-bold uppercase tracking-wider text-base">
      {num}. {title}
    </div>
    <div className="mt-3 text-[15px] leading-7 whitespace-pre-wrap font-serif-display">{children}</div>
  </div>
);

export default function PreviewTab({ caseData }) {
  const marketplaceEnabled = useMarketplaceEnabled();
  const intake = caseData.intake || {};
  const qq = caseData.questionnaire || {};
  const parties = caseData.parties || [];
  const plaintiffs = parties.filter((p) => (p.role || "").toLowerCase() === "plaintiff");
  const defendants = parties.filter((p) => (p.role || "").toLowerCase() === "defendant");

  const downloadFile = async (kind) => {
    track(kind === "pdf" ? "export_pdf" : "export_docx", { case_id: caseData.case_id });
    // Use credentialed fetch and trigger download
    const r = await fetch(`${API}/cases/${caseData.case_id}/export/${kind}`, { credentials: "include" });
    if (!r.ok) {
      alert("Export failed");
      return;
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `complaint_${caseData.case_id}.${kind}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif-display text-3xl text-slate-900">Preview & Export</h2>
          <p className="text-slate-600 mt-1">Review your draft complaint and export to PDF or DOCX.</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="label-tiny">Step 8 of 8</span>
          <div className="flex gap-2">
          <Button onClick={() => window.print()} variant="outline" className="rounded-none border-slate-300" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
          <Button onClick={() => downloadFile("docx")} variant="outline" className="rounded-none border-slate-300" data-testid="export-docx-btn">
            <FileDoc className="w-4 h-4 mr-1.5" /> Export DOCX
          </Button>
          <Button onClick={() => downloadFile("pdf")} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" data-testid="export-pdf-btn">
            <FilePdf className="w-4 h-4 mr-1.5" /> Export PDF
          </Button>
          </div>
        </div>
      </div>

      <div className="bg-slate-100 p-4 md:p-10 print:bg-white print:p-0">
        <div className="max-w-3xl mx-auto bg-white border border-slate-300 paper-shadow p-10 md:p-16 print:shadow-none print:border-0" data-testid="document-preview">
          <div className="text-center font-serif-display font-bold uppercase tracking-widest text-sm">
            {intake.court_name || "[Court Name]"}
            {intake.court_state ? ` — ${intake.court_state}` : ""}
          </div>
          <div className="border-b border-slate-300 mt-6" />

          <div className="grid grid-cols-2 gap-6 mt-6">
            <div className="text-[15px] leading-7 font-serif-display">
              {plaintiffs.length === 0
                ? "[Plaintiff(s)]"
                : plaintiffs.map((p) => p.name).join(", ")},<br />
              <span className="ml-8">Plaintiff(s),</span>
              <div className="my-3">v.</div>
              {defendants.length === 0
                ? "[Defendant(s)]"
                : defendants.map((p) => p.name).join(", ")},<br />
              <span className="ml-8">Defendant(s).</span>
            </div>
            <div className="border-l border-slate-300 pl-6 text-sm font-mono-tech">
              <div>Case No.: {intake.case_number || "[TO BE ASSIGNED]"}</div>
              <div className="mt-3 uppercase">Complaint</div>
              <div className="mt-3">Jury Trial Demanded: ___</div>
            </div>
          </div>

          <div className="border-b border-slate-300 mt-6" />

          <div className="text-center font-serif-display font-bold uppercase tracking-widest text-base mt-8">
            COMPLAINT
          </div>

          <Section num="I" title="Parties">
            {parties.length === 0
              ? "[Parties not specified.]"
              : parties.map((p, i) => (
                  <div key={p.id || i} className="mb-3">
                    {i + 1}. {p.name || "[Name]"} is a {p.role} in this action.{" "}
                    {p.description} {p.address ? `Address: ${p.address}.` : ""}
                  </div>
                ))}
          </Section>

          <Section num="II" title="Jurisdiction and Venue">
            This Court has jurisdiction over this matter pursuant to applicable {intake.jurisdiction || "[Jurisdiction]"} law.
            Venue is proper in {intake.court_name || "[Court]"} because the events giving rise to the claims occurred in{" "}
            {qq.incident_location || "[location of incident]"}.
          </Section>

          <Section num="III" title="Factual Allegations">
            {caseData.drafted_facts || qq.summary || "[Facts to be drafted. Use the Drafting tab to generate this section.]"}
          </Section>

          <Section num="IV" title="Causes of Action">
            {caseData.drafted_claims || `COUNT I — ${qq.claim_type || "[Claim Type]"}\n[Cause of action to be drafted.]`}
          </Section>

          <Section num="V" title="Prayer for Relief">
            {caseData.drafted_relief || qq.relief_requested || "[Relief to be drafted.]"}
          </Section>

          {(caseData.evidence || []).length > 0 && (
            <div className="mt-10">
              <div className="text-center font-serif-display font-bold uppercase tracking-wider text-base">List of Exhibits</div>
              <div className="mt-3 text-[15px] leading-7 font-serif-display space-y-1">
                {(caseData.evidence || []).map((ev, i) => {
                  const letter = i < 26 ? String.fromCharCode(65 + i) : String.fromCharCode(65 + Math.floor(i / 26) - 1) + String.fromCharCode(65 + (i % 26));
                  return (
                    <div key={ev.id}>
                      <span className="font-semibold">Exhibit {letter}</span> — {ev.label}
                      {ev.description ? ` — ${ev.description}` : ""}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-12 text-[15px] leading-7 font-serif-display">
            Respectfully submitted,
            <div className="mt-12">_______________________________</div>
            <div>[Signature]</div>
            <div>[Printed Name]</div>
            <div>[Address]</div>
            <div>[Phone] | [Email]</div>
          </div>

          <div className="mt-10 text-xs italic text-slate-600 border-t border-slate-300 pt-4">
            DISCLAIMER: This platform provides document drafting assistance only and does not offer legal advice. Users are
            responsible for reviewing all content before filing. This document was generated by Prose.claims, an automated drafting
            tool, and must be reviewed before filing.
          </div>
        </div>
      </div>

      {marketplaceEnabled && <AdsCards placement="post-draft" />}
      <WhyThisMatters step="preview" />
    </div>
  );
}
